import { api } from "./Api";
import { getLang } from "../i18n/lang";

/**
 * Monta a rota já no idioma escolhido.
 *
 * O interceptor em Services/Api.ts também troca o segmento de locale, então
 * isto é redundante no caminho feliz — de propósito. Estas duas chamadas
 * terminam numa página hospedada pela Stripe, fora do nosso controle: se o
 * idioma sair errado daqui, não há tela nossa para corrigir depois, o
 * usuário simplesmente cai num checkout em outra língua. Caro o bastante
 * para não depender de um único mecanismo.
 */
const rota = (caminho: string) => `/${getLang()}${caminho}`;

const authHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

// ─── Plans ────────────────────────────────────────────────────────────────────

export const getPlans = (offset = 0, limit = 100) =>
  api.get("/pt_BR/plan", { ...authHeader(), params: { offset, limit } });

export const getPlanById = (id: string) =>
  api.get(`/pt_BR/plan/${id}`, authHeader());

export const postPlan = (data: { name: string; price: string }) =>
  api.post("/admin/plan", data, authHeader());

export const putPlan = (id: string, data: { name?: string; price?: string }) =>
  api.put(`/admin/plan/${id}`, data, authHeader());

export const deletePlan = (id: string) =>
  api.delete(`/admin/plan/${id}`, authHeader());

// ─── Establishment users (admin) ──────────────────────────────────────────────

export const getEstabilishmentUsers = (
  offset = 0,
  limit = 20,
  email = "",
  fullName = ""
) => {
  const params: Record<string, any> = { offset, limit };
  if (email)    params["criteria[email]"]    = email;
  if (fullName) params["criteria[fullName]"] = fullName;
  return api.get("/admin/estabilishment_user", { ...authHeader(), params });
};

export const moderateEstabilishmentUser = (userId: string, isModerated: boolean) =>
  api.put(`/admin/user/${userId}/moderate`, { isModerated }, authHeader());

/**
 * Registra o resultado da análise manual dos documentos (docs + selfie).
 *
 * APROVAR libera imediatamente as áreas de Banner e Notificações Push do
 * plano Connection — o acesso ao painel já estava liberado desde o cadastro.
 *
 * RECUSAR mantém o acesso ao painel e envia o motivo por e-mail, para o
 * usuário reenviar os documentos.
 */
/**
 * Analisa os documentos de UMA unidade (vínculo usuário↔estabelecimento).
 *
 * Diferente de reviewEstabilishmentUserDocuments, que decide pela conta
 * inteira, aqui cada estabelecimento tem a própria análise — necessário
 * quando uma conta aprovada cadastra uma unidade nova.
 */
export const reviewUserEstabilishmentDocuments = (
  userEstabilishmentId: string,
  status: "approved" | "rejected",
  reason?: string,
) =>
  api.put(
    `/admin/user_estabilishment/${userEstabilishmentId}/documents`,
    { status, reason: reason ?? null },
    authHeader(),
  );

/**
 * @deprecated Resquício do modelo antigo (1 status por usuário). O endpoint
 * `/admin/user_estabilishment/{id}/documents` espera o id do VÍNCULO
 * (userEstabilishment.id), não o id do usuário — mandar userId aqui resulta
 * em 404. Use reviewUserEstabilishmentDocuments no lugar. Mantida só até
 * confirmar que não há mais nenhum outro ponto do código chamando esta função.
 */
export const reviewEstabilishmentUserDocuments = (
  userId: string,
  status: "approved" | "rejected",
  reason?: string,
) =>
  api.put(
    `/admin/user_estabilishment/${userId}/documents`,
    { status, reason: reason ?? null },
    authHeader(),
  );

// ─── Stripe — Checkout ────────────────────────────────────────────────────────

export const postStripeCheckout = (planId: string, userEstabilishmentId: string) =>
  api.post(
    rota("/stripe/checkout"),
    { plan: planId, userEstabilishment: userEstabilishmentId },
    authHeader()
  );

// ─── Stripe — Billing Portal ──────────────────────────────────────────────────

export const postStripeBillingPortal = () =>
  api.post(rota("/stripe/billing_portal"), {}, authHeader());

export const getEstabilishmentUserById = (id: string) =>
  api.get(`/admin/estabilishment_user/${id}`, authHeader());

export const deleteEstabilishmentUser = (id: string) =>
  api.delete(`/admin/estabilishment_user/${id}`, authHeader());

// ─── Stripe — My Subscription ─────────────────────────────────────────────────

/** Retorna a assinatura atual do usuário logado (ou data: null se não houver) */
export const getMySubscription = () =>
  api.get("/pt_BR/stripe/subscription", authHeader());

// ─── UserEstabilishment — My list ─────────────────────────────────────────────

/** Retorna os estabelecimentos vinculados ao usuário logado */
export const getMyEstabilishments = () =>
  api.get("/pt_BR/user_estabilishment/my", authHeader());

/** Atualiza Instagram e Site do estabelecimento (modal de detalhes) */
export const updateUserEstabilishmentProfile = (
  id: string,
  data: { instagram: string | null; site: string | null }
) =>
  api.put(`/pt_BR/user_estabilishment/${id}/profile`, data, authHeader());